import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { api } from '../api/cliente';
import {
  Alerta,
  Cartao,
  CabecalhoDaPagina,
  Carregando,
  Indicador,
  TituloDeSecao,
} from '../componentes/base';
import { Seletor } from '../componentes/campos';
import { EvolucaoRecebimentos, type PontoDaEvolucao } from '../componentes/EvolucaoRecebimentos';
import { Funil, type FaseDoFunil } from '../componentes/Funil';
import { data, mesCurto, mesPorExtenso, moeda, moedaCompacta, percentual, plural } from '../util/formato';

interface Painel {
  readonly mes: string;
  readonly nomeDoMes: string;
  readonly hoje: {
    readonly mensagensNaFila: number;
    readonly parcelasVencendoHoje: number;
    readonly prazosJudiciaisProximos: number;
    readonly documentosAguardandoConferencia: number;
    readonly devedoresSemTelefone: number;
    /** Contratos com a CLÍNICA vencendo ou já vencidos, do mais urgente ao menos. */
    readonly contratos: Array<{
      id: string;
      nome: string;
      terminaEm: string | null;
      diasRestantes: number | null;
      situacao: 'vencendo' | 'vencido';
    }>;
  };
  readonly recebidoNoMes: { total: number; honorarios: number; quantidade: number };
  readonly previstoNoMes: { total: number; quantidade: number };
  readonly emAtraso: { total: number; quantidade: number };
  readonly desdeOInicio: {
    recebido: number;
    honorarios: number;
    permaneceComClinica: number;
    pagamentos: number;
    registradoAqui: number;
    informadoDasPlanilhas: number;
  };
  readonly carteira: {
    total: number;
    devedores: number;
    recuperadoTotal: number;
    taxaDeRecuperacao: number;
  };
  readonly funil: FaseDoFunil[];
  readonly evolucao: PontoDaEvolucao[];
  readonly porClinica: Array<{
    id: string;
    nome: string;
    percentualHonorarios: number;
    devedores: number;
    recebidoNoMes: number;
    honorariosNoMes: number;
  }>;
}

export function PaginaPainel() {
  /* A clínica escolhida vive na URL, como os filtros da lista de devedores: assim
     a visão de uma clínica pode ser guardada nos favoritos e sobrevive ao F5. */
  const [params, setParams] = useSearchParams();
  const clinicaId = params.get('clinicaId') ?? '';
  const mes = params.get('mes') ?? '';

  /* Doze meses para trás, que é o alcance de quem confere fechamento e compara com
     o mesmo mês do ano passado. Vazio = mês corrente, e o rótulo diz isso. */
  const meses = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return { valor: iso, rotulo: i === 0 ? `${mesCurto(iso)} (atual)` : mesPorExtenso(iso) };
  });

  const { data: clinicas } = useQuery({
    queryKey: ['clinicas', 'selecao'],
    queryFn: () => api.buscar<Array<{ id: string; nome: string }>>('/clinicas/selecao'),
  });

  const { data: painel, isLoading } = useQuery({
    queryKey: ['painel', clinicaId, mes],
    queryFn: () =>
      api.buscar<Painel>('/painel', {
        ...(clinicaId ? { clinicaId } : {}),
        ...(mes ? { mes } : {}),
      }),
  });

  const nomeDaClinica = clinicas?.find((c) => c.id === clinicaId)?.nome;

  function definirFiltro(chave: 'clinicaId' | 'mes', valor: string) {
    const novo = new URLSearchParams(params);
    if (valor) novo.set(chave, valor);
    else novo.delete(chave);
    setParams(novo, { replace: true });
  }

  if (isLoading || !painel) return <Carregando />;

  const { hoje } = painel;
  const pendencias = [
    hoje.mensagensNaFila > 0 && {
      texto: `${plural(hoje.mensagensNaFila, 'mensagem aguardando envio', 'mensagens aguardando envio')}`,
      para: '/cobrancas',
      acao: 'Abrir a fila',
    },
    hoje.prazosJudiciaisProximos > 0 && {
      texto: `${plural(hoje.prazosJudiciaisProximos, 'prazo judicial próximo', 'prazos judiciais próximos')}`,
      para: '/triagem',
      acao: 'Ver processos',
    },
    hoje.documentosAguardandoConferencia > 0 && {
      texto: `${plural(hoje.documentosAguardandoConferencia, 'documento aguardando conferência', 'documentos aguardando conferência')}`,
      para: '/devedores',
      acao: 'Conferir',
    },
    hoje.devedoresSemTelefone > 0 && {
      texto: `${plural(hoje.devedoresSemTelefone, 'devedor sem telefone', 'devedores sem telefone')} — a régua não alcança esses casos`,
      para: '/devedores',
      acao: 'Completar cadastro',
    },
    /* CONTRATO COM A CLÍNICA, e não com o devedor. Vem com o NOME de quem vence
       primeiro: a contagem sozinha mandaria procurar em quatro cartões qual é a
       clínica, e é o nome que decide se a conversa é hoje ou semana que vem. */
    hoje.contratos.length > 0 &&
      (() => {
        const primeiro = hoje.contratos[0]!;
        const dias = primeiro.diasRestantes ?? 0;
        const comoEsta =
          primeiro.situacao === 'vencido'
            ? `venceu há ${plural(Math.abs(dias), 'dia', 'dias')}`
            : dias === 0
              ? 'vence hoje'
              : `vence em ${plural(dias, 'dia', 'dias')}`;
        const resto = hoje.contratos.length - 1;
        return {
          texto:
            `Contrato da ${primeiro.nome} ${comoEsta}` +
            (resto > 0
              ? ` — e mais ${plural(resto, 'contrato próximo do fim', 'contratos próximos do fim')}`
              : ''),
          para: '/clinicas',
          acao: 'Ver contratos',
        };
      })(),
  ].filter(Boolean) as Array<{ texto: string; para: string; acao: string }>;

  return (
    <>
      <CabecalhoDaPagina
        titulo="Painel geral"
        descricao={
          nomeDaClinica
            ? `${nomeDaClinica} — ${mesPorExtenso(painel.mes).toLowerCase()}`
            : `Situação consolidada de ${mesPorExtenso(painel.mes).toLowerCase()}`
        }
        acoes={
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-44">
              <Seletor
                valor={mes}
                aoMudar={(v) => definirFiltro('mes', v)}
                vazio="Mês atual"
                opcoes={meses}
              />
            </div>
            <div className="w-60">
              <Seletor
                valor={clinicaId}
                aoMudar={(v) => definirFiltro('clinicaId', v)}
                vazio="Todas as clínicas"
                opcoes={(clinicas ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))}
              />
            </div>
          </div>
        }
      />

      {/* A tela INTEIRA passa a ser da clínica escolhida — inclusive as pendências
          do dia, que também filtram no servidor. Dizer isso em voz alta importa: sem
          o aviso, "nenhuma mensagem na fila" seria lido como o escritório vazio,
          quando é só aquela clínica que não tem nada pendente. */}
      {nomeDaClinica && (
        <p className="mb-4 text-xs text-[color:var(--texto-tenue)]">
          Tudo nesta tela é de {nomeDaClinica} — pendências, números, funil e evolução.
          Escolha “Todas as clínicas” para ver o escritório inteiro.
        </p>
      )}

      {/* O que exige ação hoje vem ANTES dos números do mês: a tela inicial existe
          para dizer o que fazer, não só para informar como foi. */}
      {pendencias.length > 0 && (
        <div className="mb-6 flex flex-col gap-2">
          {pendencias.map((p) => (
            <Alerta key={p.para + p.texto}>
              <span className="flex flex-wrap items-center justify-between gap-2">
                <span>{p.texto}</span>
                <Link
                  to={p.para}
                  className="font-medium text-[color:var(--acento-sobre-superficie)] underline underline-offset-2"
                >
                  {p.acao} →
                </Link>
              </span>
            </Alerta>
          ))}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {/* CLICÁVEL: leva à lista dos pagamentos que formam este número, no mesmo
            mês e na mesma clínica. Total que não se abre é afirmação que ninguém
            consegue conferir — e foi assim que quatro lançamentos de teste passaram
            por recebimento de verdade. */}
        <Link
          to={`/pagamentos?mes=${painel.mes}${clinicaId ? `&clinicaId=${clinicaId}` : ''}`}
          className="rounded-md transition-opacity hover:opacity-80"
          title="Ver os pagamentos deste mês"
        >
          <Indicador
            rotulo="Recuperado no mês"
            valor={moeda(painel.recebidoNoMes.total)}
            nota={`${plural(painel.recebidoNoMes.quantidade, 'pagamento', 'pagamentos')} — clique para ver quais`}
            tom="recebido"
          />
        </Link>
        <Link
          to={`/pagamentos?mes=${painel.mes}${clinicaId ? `&clinicaId=${clinicaId}` : ''}`}
          className="rounded-md transition-opacity hover:opacity-80"
          title="Ver os pagamentos e honorários deste mês"
        >
          <Indicador
            rotulo="Honorários do escritório"
            valor={moeda(painel.recebidoNoMes.honorarios)}
            nota="sobre o recuperado no mês — clique para ver"
            tom="acento"
          />
        </Link>
        <Link
          to={`/devedores?emAtraso=true${clinicaId ? `&clinicaId=${clinicaId}` : ''}`}
          className="rounded-md transition-opacity hover:opacity-80"
          title="Ver quem tem parcela em atraso"
        >
          <Indicador
            rotulo="Parcelas em atraso"
            valor={moeda(painel.emAtraso.total)}
            nota={`${plural(painel.emAtraso.quantidade, 'parcela vencida', 'parcelas vencidas')} — clique para ver`}
            tom={painel.emAtraso.quantidade > 0 ? 'vencido' : 'neutro'}
          />
        </Link>
        <Indicador
          rotulo="Previsto para o mês"
          valor={moedaCompacta(painel.previstoNoMes.total)}
          nota={plural(painel.previstoNoMes.quantidade, 'parcela a vencer', 'parcelas a vencer')}
        />
      </div>

      {/* DESDE O COMEÇO, logo abaixo do mês e visivelmente separado dele.
          O mês responde "como estamos indo"; isto responde "quanto essa carteira já
          rendeu" — a pergunta de quem renegocia contrato. Juntar os dois períodos na
          mesma faixa de cartões é o jeito mais rápido de ninguém confiar em nenhum. */}
      <Cartao className="mt-4" semPadding>
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-3 p-5">
          <div>
            <p className="text-[0.6875rem] font-medium uppercase tracking-[0.1em] text-[color:var(--texto-tenue)]">
              Desde o início da cobrança
              {nomeDaClinica ? ` · ${nomeDaClinica}` : ''}
            </p>
            {/* Um número composto que não se explica é um número em que ninguém
                confia. Quando há herança das planilhas, a linha diz quanto veio de
                cada lugar — o que o sistema apurou e o que foi informado à mão. */}
            <p className="mt-1 text-xs text-[color:var(--texto-suave)]">
              {painel.desdeOInicio.informadoDasPlanilhas > 0 ? (
                <>
                  {moeda(painel.desdeOInicio.registradoAqui)} baixados aqui em{' '}
                  {plural(painel.desdeOInicio.pagamentos, 'pagamento', 'pagamentos')} ·{' '}
                  {moeda(painel.desdeOInicio.informadoDasPlanilhas)} informados das planilhas
                </>
              ) : (
                plural(
                  painel.desdeOInicio.pagamentos,
                  'pagamento registrado',
                  'pagamentos registrados',
                )
              )}
            </p>
          </div>

          <dl className="flex flex-wrap gap-x-10 gap-y-3">
            <div>
              <dt className="text-[0.6875rem] uppercase tracking-[0.08em] text-[color:var(--texto-tenue)]">
                Recebido no total
              </dt>
              <dd
                className="tabular mt-0.5 text-xl font-semibold"
                style={{ color: 'var(--color-recebido)' }}
              >
                {moeda(painel.desdeOInicio.recebido)}
              </dd>
            </div>
            <div>
              <dt className="text-[0.6875rem] uppercase tracking-[0.08em] text-[color:var(--texto-tenue)]">
                Ficou com a clínica
              </dt>
              <dd className="tabular mt-0.5 text-xl font-semibold">
                {moeda(painel.desdeOInicio.permaneceComClinica)}
              </dd>
            </div>
            <div>
              <dt className="text-[0.6875rem] uppercase tracking-[0.08em] text-[color:var(--texto-tenue)]">
                Honorários do escritório
              </dt>
              <dd
                className="tabular mt-0.5 text-xl font-semibold"
                style={{ color: 'var(--acento)' }}
              >
                {moeda(painel.desdeOInicio.honorarios)}
              </dd>
            </div>
          </dl>
        </div>
      </Cartao>

      {/* `items-start`: sem isso o cartão do gráfico é esticado até a altura do
          funil e sobra um vazio enorme embaixo da série. */}
      <div className="mb-6 grid grid-cols-1 items-start gap-6 lg:grid-cols-[1.35fr_1fr]">
        <Cartao>
          <TituloDeSecao auxiliar="clique numa fase para filtrar a carteira">
            Funil de cobrança
          </TituloDeSecao>
          <Funil fases={painel.funil} />

          <div className="mt-5 flex flex-wrap items-baseline gap-x-6 gap-y-1 border-t pt-4 text-sm">
            <span className="text-[color:var(--texto-suave)]">
              Carteira total{' '}
              <strong className="tabular font-semibold text-[color:var(--texto)]">
                {moedaCompacta(painel.carteira.total)}
              </strong>{' '}
              em {plural(painel.carteira.devedores, 'devedor', 'devedores')}
            </span>
            <span className="text-[color:var(--texto-suave)]">
              Já recuperado{' '}
              <strong className="tabular font-semibold text-[color:var(--color-recebido)]">
                {percentual(painel.carteira.taxaDeRecuperacao)}
              </strong>
            </span>
          </div>
        </Cartao>

        <Cartao>
          <TituloDeSecao auxiliar="últimos 6 meses">Evolução dos recebimentos</TituloDeSecao>
          <EvolucaoRecebimentos pontos={painel.evolucao} />
        </Cartao>
      </div>

      {painel.porClinica.length > 0 && (
        <Cartao semPadding>
          <div className="p-5 pb-0">
            <TituloDeSecao auxiliar={mesPorExtenso(painel.mes)}>
              Resultado por clínica
            </TituloDeSecao>
          </div>
          <div className="rolagem-lateral">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-[0.6875rem] uppercase tracking-[0.08em] text-[color:var(--texto-tenue)]">
                  <th className="px-5 py-2.5 font-medium">Clínica</th>
                  <th className="px-5 py-2.5 text-right font-medium">Devedores</th>
                  <th className="px-5 py-2.5 text-right font-medium">Recuperado</th>
                  <th className="px-5 py-2.5 text-right font-medium">Honorários</th>
                </tr>
              </thead>
              <tbody>
                {painel.porClinica.map((clinica) => (
                  <tr key={clinica.id} className="border-b last:border-0">
                    <td className="px-5 py-3">
                      <Link
                        to={`/devedores?clinicaId=${clinica.id}`}
                        className="font-medium text-[color:var(--texto)] hover:text-[color:var(--acento)]"
                      >
                        {clinica.nome}
                      </Link>
                      <span className="ml-2 text-xs text-[color:var(--texto-tenue)]">
                        {percentual(clinica.percentualHonorarios)} de êxito
                      </span>
                    </td>
                    <td className="tabular px-5 py-3 text-right text-[color:var(--texto-suave)]">
                      {clinica.devedores}
                    </td>
                    <td className="tabular px-5 py-3 text-right">
                      {moeda(clinica.recebidoNoMes)}
                    </td>
                    <td className="tabular px-5 py-3 text-right font-semibold text-[color:var(--acento)]">
                      {moeda(clinica.honorariosNoMes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Cartao>
      )}

      <p className="mt-6 text-xs text-[color:var(--texto-tenue)]">
        Vencem hoje: {plural(hoje.parcelasVencendoHoje, 'parcela', 'parcelas')} ·{' '}
        {data(new Date().toISOString().slice(0, 10))}
      </p>
    </>
  );
}
